import React, { useEffect } from 'react';
import { Button } from 'reactstrap';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import usePlayback from '../../../../hooks/usePlayback';

const STEP_MESSAGES = {
    preparing: 'Preparing keys...',
    loading: 'Loading content...',
    starting: 'Starting playback...',
};

const PlayButton = ({ order, label = 'PLAY MOVIE', organization, organizationContent, icon = 'fa-play' }) => {
    const { playing } = useSelector(state => state.playMovie);
    const movieId = order?.movie?._id || order?.movie;
    const orderId = order?._id;
    const version = order?.version;
    const isTrailer = !!order?.isTrailer;

    const { step, stepProgress, error, isLoading, isLocked, startPlayback, clearError } = usePlayback(orderId, movieId);

    // Show error toast when error occurs
    useEffect(() => {
        if (error) {
            toast.error(error);
            clearError();
        }
    }, [error, clearError]);

    if (!order) return null;

    // Check if TMS is running
    const isTmsRunning = organization?.tmsSettings?.status === 'running';

    // Check if content is on mediablock
    const isContentOnMediablock = () => {
        if (!version?.uuid || !organizationContent?.length) return false;
        return organizationContent.some(
            content => content.cpl_uuid === version.uuid &&
                       content.location === 'mediablock' &&
                       content.type === 'CPL'
        );
    };

    // Get button state
    const getButtonState = () => {
        if (playing) {
            return {
                disabled: true,
                label: label,
                icon: icon,
                color: 'secondary',
                tooltip: 'Player is currently busy'
            };
        }
        if (!isTmsRunning) {
            return {
                disabled: true,
                label: 'TMS not running',
                icon: 'fa-exclamation-triangle',
                color: 'secondary'
            };
        }
        if (!isContentOnMediablock()) {
            return {
                disabled: true,
                label: isTrailer ? 'Trailer is not on mediablock' : 'Movie is not on mediablock',
                icon: 'fa-hourglass-half',
                color: 'secondary'
            };
        }
        return {
            disabled: false,
            label: label,
            icon: icon,
            color: 'primary'
        };
    };

    const buttonState = getButtonState();
    const isPlaybackLoading = !!step || isLoading;

    const handleClick = () => {
        if (!buttonState.disabled && !isPlaybackLoading && !isLocked) {
            startPlayback();
        }
    };

    const displayLabel = isPlaybackLoading
        ? `${STEP_MESSAGES[step] || 'Loading...'}${stepProgress != null ? ` ${stepProgress}%` : ''}`
        : buttonState.label;

    const button = (
        <Button
            color={buttonState.color}
            outline
            onClick={handleClick}
            disabled={buttonState.disabled || isPlaybackLoading || isLocked}
        >
            {isPlaybackLoading ? (
                <span className="fas fa-spinner fa-spin me-2" />
            ) : (
                <span className={`fas ${buttonState.icon} me-2`} />
            )}
            {displayLabel}
        </Button>
    );

    if (buttonState.tooltip) {
        return <span title={buttonState.tooltip}>{button}</span>;
    }

    return button;
};

export default PlayButton;
